import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Manual link picker — user chooses which existing task must finish first.
 * Filters:
 *  - Only tasks the user owns (backend already filters by owner_id)
 *  - Excludes self (same id as current task)
 *  - Excludes tasks that already form a cycle (backend will 400; we pre-hide
 *    tasks currently waiting on the current task directly)
 *
 * After a pick:
 *  - Shows a confirmation: "Ovaj zadatak će se aktivirati kada se završi: [naziv]."
 *  - Save issues PATCH /api/tasks/{currentId} with waiting_for_task_ids
 *    appended.
 */
export default function ManualLinkPickerDialog({
  open,
  onOpenChange,
  currentTask,           // { id, title, waiting_for_task_ids }
  onLinked,
}) {
  const [q, setQ] = useState("");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setQ(""); setTasks([]); setPicked(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tasks?filter=open");
        setTasks(data || []);
      } catch { setTasks([]); }
      setLoading(false);
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const excludeIds = new Set([
      currentTask?.id,
      ...(currentTask?.waiting_for_task_ids || []),
    ]);
    // Also pre-hide tasks that already wait on the current one (direct cycle)
    return (tasks || [])
      .filter((t) => !excludeIds.has(t.id))
      .filter((t) => !(t.waiting_for_task_ids || []).includes(currentTask?.id))
      .filter((t) => t.status !== "done")
      .filter((t) => (q ? t.title.toLowerCase().includes(q.toLowerCase()) : true))
      .slice(0, 30);
  }, [tasks, currentTask, q]);

  const confirm = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const nextIds = Array.from(new Set([...(currentTask.waiting_for_task_ids || []), picked.id]));
      await api.patch(`/tasks/${currentTask.id}`, {
        waiting_for_task_ids: nextIds,
        unlock_note: currentTask.unlock_note || `Kada završi „${picked.title}"`,
      });
      toast.success("Redosled sačuvan.");
      onOpenChange(false);
      onLinked?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="manual-picker-dialog"
        className="bg-white max-w-md max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            Šta treba prvo da se završi?
          </DialogTitle>
        </DialogHeader>

        {!picked ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-ink-muted" />
              <Input
                data-testid="manual-picker-search"
                placeholder="Traži zadatak..."
                className="pl-8"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto border border-line rounded-md divide-y divide-line bg-white">
              {loading && (
                <div className="text-xs text-ink-muted p-2 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> učitavam...
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="text-xs text-ink-muted p-3">
                  Nema odgovarajućih zadataka{q ? ` za „${q}"` : ""}.
                </div>
              )}
              {filtered.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`manual-pick-${t.id}`}
                  onClick={() => setPicked(t)}
                  className="w-full text-left text-sm px-3 py-2 hover:bg-surface-secondary"
                >
                  {t.title}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-ink-muted">
              Ne prikazujemo zadatke koji već čekaju na ovaj — sistem sam štiti od kruženja.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border border-forest/30 bg-forest/[0.04] p-4 flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-forest mt-0.5 shrink-0" />
              <div className="text-sm text-ink">
                Ovaj zadatak će se aktivirati kada se završi:{" "}
                <b className="text-forest">{picked.title}</b>.
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                className="btn-ghost"
                onClick={() => setPicked(null)}
                disabled={saving}
                data-testid="manual-picker-back"
              >
                Nazad
              </button>
              <button
                className="btn-primary"
                onClick={confirm}
                disabled={saving}
                data-testid="manual-picker-confirm"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Potvrdi
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
